import React, { useState } from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import { FieldHeader, FieldErrors, FieldHelpText } from './FieldParts';
import {
  useElementValue,
  useIsEditor,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';

interface Props {
  element: DocumentElementType;
}

interface GeoValue {
  lat: number;
  lng: number;
}

const isGeoValue = (v: unknown): v is GeoValue =>
  !!v &&
  typeof v === 'object' &&
  typeof (v as GeoValue).lat === 'number' &&
  typeof (v as GeoValue).lng === 'number';

const fmt = (v: GeoValue): string => `${v.lat.toFixed(4)}, ${v.lng.toFixed(4)}`;

const MAP_BG =
  'linear-gradient(135deg,#e4ebf3,#d4e4ef)';
const GRID_BG =
  'linear-gradient(rgba(255,255,255,.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.5) 1px,transparent 1px)';

/**
 * Geo tag (Elements Library): capture the filler's location. "Use my
 * location" reads the browser geolocation and stores {lat,lng}; a map panel
 * shows a pin once captured, with the coordinates in the footer.
 */
const GeoTag: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<GeoValue | null>(element.id);
  const isEditor = useIsEditor();
  const isReadOnly = useIsReadOnly();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const data = element.data as {
    label?: string;
    subLabel?: string;
    helpText?: string;
    required?: boolean;
    hidden?: boolean;
  };

  const label = data?.label || 'Geo tag';
  const required = !!data?.required;
  const geo = isGeoValue(value) ? value : null;

  const clear = () => {
    setValue(null);
    setConfirming(false);
    setError('');
    setTouched(true);
  };

  const capture = () => {
    setTouched(true);
    setError('');
    setConfirming(false);
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setError('Location is not available on this device.');
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setValue({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLoading(false);
      },
      (err) => {
        setError(
          err.code === err.PERMISSION_DENIED
            ? 'Location permission denied.'
            : 'Could not get your location.',
        );
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const errors =
    isEditor || !touched || !required || geo ? [] : ['This field is required'];

  return (
    <div className="space-y-2">
      <FieldHeader
        htmlFor={element.id}
        label={label}
        subLabel={data?.subLabel}
        required={required}
      />
      <div className="overflow-hidden rounded-[12px] border border-[var(--forms-border)]">
        <div className="relative h-[120px]" style={{ background: MAP_BG }}>
          <div
            className="absolute inset-0"
            style={{ backgroundImage: GRID_BG, backgroundSize: '26px 26px' }}
          />
          {geo && (
            <span
              className="absolute left-1/2 top-[46%]"
              style={{ transform: 'translate(-50%,-100%)' }}
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="#c0303c" stroke="#fff" strokeWidth={1.5} aria-hidden>
                <path d="M12 21s-7-6.5-7-11a7 7 0 0 1 14 0c0 4.5-7 11-7 11z" />
                <circle cx="12" cy="10" r="2.5" fill="#fff" />
              </svg>
            </span>
          )}
        </div>
        <div className="flex items-center gap-[8px] border-t border-[#eef1f4] bg-[var(--forms-addon-bg)] px-[13px] py-[10px]">
          <span className="flex-none text-[var(--forms-accent,var(--forms-accent-default))]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="12" cy="12" r="9" />
              <line x1="12" y1="2" x2="12" y2="5" />
              <line x1="12" y1="19" x2="12" y2="22" />
              <line x1="2" y1="12" x2="5" y2="12" />
              <line x1="19" y1="12" x2="22" y2="12" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </span>
          {confirming ? (
            <>
              <span className="text-[12.5px] font-semibold text-[var(--forms-ink)]">
                Clear location?
              </span>
              <span className="flex-1" />
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="text-[12px] font-bold text-[var(--forms-muted-soft)] transition-colors hover:text-[var(--forms-ink)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={clear}
                className="text-[12px] font-bold text-[var(--forms-rail-red)] transition-opacity hover:opacity-80"
              >
                Clear
              </button>
            </>
          ) : (
            <>
              <span className="text-[12.5px] font-semibold text-[#3a4252]">
                {geo ? fmt(geo) : 'No location set'}
              </span>
              <span className="flex-1" />
              {!isReadOnly && geo && (
                <button
                  type="button"
                  onClick={() => setConfirming(true)}
                  className="text-[12px] font-bold text-[var(--forms-muted-soft)] transition-colors hover:text-[var(--forms-rail-red)]"
                >
                  Clear
                </button>
              )}
              {!isReadOnly && (
                <button
                  type="button"
                  onClick={capture}
                  disabled={loading}
                  className="text-[12px] font-bold text-[var(--forms-accent,var(--forms-accent-default))] transition-opacity hover:opacity-80 disabled:opacity-50"
                >
                  {loading
                    ? 'Locating…'
                    : geo
                      ? 'Update location'
                      : 'Use my location'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {error && (
        <p className="text-[12px] text-[var(--forms-rail-red)]">{error}</p>
      )}
      <FieldErrors errors={errors} />
      <FieldHelpText>{data?.helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default GeoTag;
